import {useMemo} from 'react';
import {STEP_PROCESSING_STATUS} from '../AurPizzaTrackerProgress';
import AnimatedProgreesBar, {
  NonAnimatedProgressBar,
} from './AnimatedProgressBar';
import PulseStep from './PulseStep';
import {t} from 'i18next';

interface Props {
  stepNo: string;
  isLastStep: boolean;
  status?: string;
  progressBarWidth: number;
  pulseSize: number;
  stepProcessingStatus: STEP_PROCESSING_STATUS;
  statusLabel: string;
  errorLabel?: string;
  bottomContentAriaLabel?: string | null;
}

const AnimatedProgressSingleStep: React.FC<Props> = ({
  isLastStep,
  stepNo,
  status,
  progressBarWidth,
  pulseSize,
  stepProcessingStatus,
  statusLabel,
  errorLabel,
  bottomContentAriaLabel,
}) => {
  const ariaLabel = useMemo(() => {
    const stringPath = 'common.pizzaTracker.ariaLabel.';
    let label = '';
    switch (stepProcessingStatus) {
      case 'NotStarted':
        label = 'notStarted';
        break;
      case 'Completed':
      case 'Missing':
        label = 'completed';
        break;
      case 'InProgress':
        label = 'inProgress';
        break;
      case 'Failed':
        label = 'failed';
        break;
      default:
        label = 'unknown';
        break;
    }

    let result = `${t('common.pizzaTracker.step')} ${stepNo} ${
      stepProcessingStatus === 'Failed' && (errorLabel ?? '') !== ''
        ? `${statusLabel} ${errorLabel}`
        : t(stringPath + label, {
            status: statusLabel,
          })
    }`;
    if (bottomContentAriaLabel) result += `${bottomContentAriaLabel}`;

    return result;
  }, [
    stepProcessingStatus,
    statusLabel,
    t,
    bottomContentAriaLabel,
    errorLabel,
  ]);

  const renderProgress = () => {
    if (stepProcessingStatus === 'InProgress')
      /** Render Animated progress bar if step is in progress */
      return <AnimatedProgreesBar status={status} width={progressBarWidth} />;
    else {
      /** Render Non Animated progress bar if step is not started or completed */
      return (
        <NonAnimatedProgressBar
          stepProcessingStatus={stepProcessingStatus}
          status={status}
          width={progressBarWidth}
        />
      );
    }
  };

  return (
    <div
      className="display-flex"
      aria-label={ariaLabel}
      aria-current={stepProcessingStatus === 'InProgress' ? 'step' : false}
      aria-disabled={stepProcessingStatus !== 'InProgress'}
      /**
       * sonar warning can't be fix here this tabindex required to configure
       * on none clickable element so on press of tab, focus should show here
       */
      tabIndex={0}
    >
      {/** Pulse View Render  */}
      <PulseStep
        currentStepNo={stepNo}
        isLastStep={isLastStep}
        status={status}
        size={pulseSize}
        stepProcessingStatus={stepProcessingStatus}
      />
      {!isLastStep && renderProgress()}
    </div>
  );
};

export default AnimatedProgressSingleStep;
